import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-brave-modal';
import { toast } from 'react-toastify';
import { format, parseISO, subDays } from 'date-fns';
import { useCurrentEffect } from '../../../lib/use-current-effect';

import { useSocketIo } from '../../../hooks/socketIo';
import { useAuth } from '../../../hooks/auth';
import {
  AlertRow,
  AlertsSummary,
  DeviceInactivity,
} from '../../../entities/Alert';
import { HealthParameterThreshold } from '../../../entities/HealthParameter';
import {
  getAlertsInactivity,
  getAlertsSummary,
  listAlerts,
} from '../../../services/alerts';
import { listThresholds } from '../../../services/healthParameters';
import { alertChartConfig } from '../../../utils/alertChart';
import AlertCategoryEnum from '../../../enums/AlertCategoryEnum';
import AlertSeverityEnum from '../../../enums/AlertSeverityEnum';
import AlertSituationEnum from '../../../enums/AlertSituationEnum';
import MainAreasEnum from '../../../enums/MainAreasEnum';

import DashboardTemplate from '../../../templates/Dashboard';
import PageTree from '../../../components/PageTree';
import { ElementPageTree } from '../../../components/PageTree/types';
import Pagination from '../../../components/Pagination';
import Spinner from '../../../components/Spinner';
import CardContentNothingToDisplay from '../../../components/CardContentNothingToDisplay';
import CloudSketchesImage from '../../../assets/images/cloud-sketches.svg';

import ParametersModal from './ParametersModal';
import Summary from './Summary';
import AlertsTable from './Table';
import TreatModal from './TreatModal';
import AlertDetailsModal from './AlertDetailsModal';

import {
  Card,
  CardTitle,
  Container,
  EmptyBox,
  FailureBox,
  Filters,
  GhostButton,
  Header,
  SpinnerContainer,
  TopBlocks,
} from './styles';

const PER_PAGE = 20;

const pages: ElementPageTree[] = [{ name: 'Alertas' }];

/**
 * O recorte de tempo. **"Todo o período" é o padrão, e não "24 horas"**: a fila
 * é de alertas pendentes, e um pendente de anteontem não pode sumir porque o filtro
 * abriu numa janela curta.
 */
const PERIODS: { value: string; label: string; days: number | null }[] = [
  { value: '', label: 'Todo o período', days: null },
  { value: '1', label: 'Últimas 24 horas', days: 1 },
  { value: '7', label: 'Últimos 7 dias', days: 7 },
  { value: '30', label: 'Últimos 30 dias', days: 30 },
];

/**
 * O filtro "Situação", que vale para a lista **e** para os cartões do topo.
 * "Gravados sem fila" é o estado `registrado`: alerta de paciente de tenant sem
 * equipe, que não é trabalho de ninguém mas precisa ser consultável.
 */
const SITUATIONS: { value: AlertSituationEnum | ''; label: string }[] = [
  { value: AlertSituationEnum.PENDING, label: 'Não resolvidos' },
  { value: AlertSituationEnum.RESOLVED, label: 'Resolvidos' },
  { value: AlertSituationEnum.RECORDED, label: 'Gravados sem fila' },
  { value: '', label: 'Todos' },
];

/**
 * A home de Alertas — tarefa **8.3**, antecipada da Fase 8.
 *
 * O que a spec (`02-alertas.md`) fixa e esta tela obedece:
 *
 * - **Nenhum nome de paciente**, só ID. A tela é projetável em telão.
 * - **Gravidade e Categoria em colunas separadas** (AJ-01), nunca um badge só.
 * - As duas ações — **WhatsApp** e **Visualizar** — em toda linha, para todo tipo e
 *   gravidade de alerta.
 *
 * ## Uma tela só (2026-09-24)
 *
 * O antigo painel de contingência (**8.4**, `/alerts/simple`) foi absorvido aqui. Ele era uma projeção desta mesma fila (`origin=relogio`), e duas
 * telas mostrando os mesmos alertas deixavam o profissional sem saber qual era a
 * fila de verdade. O que só ele mostrava veio junto: rótulo do parâmetro, régua
 * ("Ver parâmetros" e o trecho por linha), idade, pessoas envolvidas, filtro de
 * período e a tela cheia para telão. A rota antiga redireciona para cá.
 */
const AlertsList: React.FC = () => {
  const history = useHistory();
  const { socket } = useSocketIo();
  const { showModal } = useModal();
  const { user } = useAuth();

  /**
   * Abrir atendimento exige cadastro de profissional. Decidido aqui, uma vez, para a
   * tabela e o informativo desligarem o "Tratar" com o motivo em vez de deixar a
   * pessoa preencher a janela e ser recusada no fim.
   */
  const canTreat = !!user?.professional_id;

  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState<AlertsSummary>();
  const [inactivity, setInactivity] = useState<DeviceInactivity>();
  const [thresholds, setThresholds] = useState<HealthParameterThreshold[]>([]);
  /**
   * Falha de consulta, separada de "veio vazio". Sem isto a tela de erro e a de
   * "nenhum alerta" eram a mesma, e a fila parecia limpa quando não se sabia.
   */
  const [listFailed, setListFailed] = useState(false);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [severity, setSeverity] = useState<AlertSeverityEnum | ''>('');
  const [category, setCategory] = useState<AlertCategoryEnum | ''>('');
  const [periodDays, setPeriodDays] = useState('');
  const [situation, setSituation] = useState<AlertSituationEnum | ''>(
    AlertSituationEnum.PENDING,
  );
  const [fullscreen, setFullscreen] = useState(false);
  /**
   * Muda de valor quando o socket avisa que algo entrou ou mudou. É o gatilho de
   * recarga — e não um `push` na lista local — porque a ordenação da fila é
   * (gravidade, prioridade, idade) e mora no back: inserir na ponta poria um alerta
   * moderado acima de um grave que já estava lá.
   */
  const [liveTick, setLiveTick] = useState(0);

  /**
   * O resumo sempre tem janela: sem `from`, o back usa os últimos 30 dias, porque a
   * variação "vs. período anterior" precisa de uma. Só a lista fica sem piso.
   */
  const summaryPeriodLabel =
    PERIODS.find((period) => period.value === periodDays && period.days)
      ?.label ?? 'Últimos 30 dias';

  /** `undefined` em "Todo o período": sem `from`, a lista não tem piso de data. */
  const from = useMemo(() => {
    const days = PERIODS.find((period) => period.value === periodDays)?.days;

    return days ? subDays(new Date(), days).toISOString() : undefined;
  }, [periodDays]);

  useCurrentEffect(
    (isCurrent) => {
      setLoading(true);

      listAlerts(
        {
          from,
          severity: severity || undefined,
          category: category || undefined,
          situation: situation || undefined,
        },
        page,
        PER_PAGE,
      )
        .then((response) => {
          if (!isCurrent()) {
            return;
          }

          setAlerts(response.alerts);
          setTotal(response.total);
          setListFailed(false);
          setLoading(false);
        })
        .catch(() => {
          if (!isCurrent()) {
            return;
          }

          // A lista antiga sai: mantê-la mostraria uma fila que não se sabe se
          // ainda é verdade, sem dizer que é velha.
          setAlerts([]);
          setTotal(0);
          setListFailed(true);
          setLoading(false);
        });
    },
    [from, severity, category, situation, page, liveTick],
  );

  useCurrentEffect(
    (isCurrent) => {
      // Uma bandeira para os dois: é o mesmo bloco da tela, e basta um falhar para
      // os números do topo não serem confiáveis.
      Promise.all([
        getAlertsSummary({ from, situation: situation || undefined }),
        getAlertsInactivity(),
      ])
        .then(([summaryData, inactivityData]) => {
          if (isCurrent()) {
            setSummary(summaryData);
            setInactivity(inactivityData);
            setSummaryFailed(false);
          }
        })
        .catch(() => {
          if (isCurrent()) {
            setSummary(undefined);
            setInactivity(undefined);
            setSummaryFailed(true);
          }
        });
    },
    [from, situation, liveTick],
  );

  useCurrentEffect((isCurrent) => {
    listThresholds().then((data) => {
      if (isCurrent()) {
        setThresholds(data);
      }
    });
  }, []);

  /**
   * Tempo real, no mesmo padrão que o painel antigo usava para `newNotices`.
   *
   * Três chaves e não uma: o back distingue alerta **novo**, alerta **reforçado**
   * (mesmo quadro insistindo, prioridade subindo) e alerta que **piorou** de faixa.
   * A tela recarrega nos três, mas só avisa nos dois que merecem atenção — um
   * reforço a cada minuto de uma PA alta sustentada viraria ruído e a equipe
   * aprenderia a ignorar o aviso.
   */
  useCurrentEffect(
    (isCurrent) => {
      if (!socket) {
        // Sempre devolve um cleanup: o `useCurrentEffect` aceita `void`, mas um
        // retorno condicional faz o tipo virar `void | (() => void)` e o compilador
        // reclamar no call site.
        return () => undefined;
      }

      const recarregar = () => {
        if (isCurrent()) {
          setLiveTick((tick) => tick + 1);
        }
      };

      const aoChegar = () => {
        toast.info('Novo alerta na fila.');
        recarregar();
      };

      const aoPiorar = () => {
        toast.warn('Um alerta piorou de gravidade.');
        recarregar();
      };

      socket.on('alert:new', aoChegar);
      socket.on('alert:worsened', aoPiorar);
      socket.on('alert:reinforced', recarregar);

      return () => {
        socket.off('alert:new', aoChegar);
        socket.off('alert:worsened', aoPiorar);
        socket.off('alert:reinforced', recarregar);
      };
    },
    [socket],
  );

  /**
   * O prontuário, já no parâmetro e no dia do alerta — ver `initialMetric` na
   * Evolução. Alerta sem gráfico (silêncio de dispositivo, perda de peso) abre o
   * prontuário como antes.
   */
  const handleOpenPatient = useCallback(
    (alert: AlertRow) => {
      const params = new URLSearchParams({ id: alert.patientId });
      const metricType = alertChartConfig(alert.parameter)?.metricType;

      if (metricType) {
        params.set('metric', metricType);
        params.set(
          'date',
          format(parseISO(alert.lastTriggeredAt), 'yyyy-MM-dd'),
        );
      }

      history.push(`/patient/profile?${params.toString()}`);
    },
    [history],
  );

  /**
   * "Tratar alerta" (tarefa 5.2). O modal abre o atendimento e vincula; aqui só
   * recarregamos, porque o alerta muda de estado e sai da fila — reusar o
   * `liveTick` faz a lista, os contadores e o painel de inatividade virem juntos,
   * pelo mesmo caminho do tempo real.
   */
  const handleTreat = useCallback(
    (alert: AlertRow) => {
      showModal({
        type: 'custom',
        data: (
          <TreatModal
            alert={alert}
            onTreated={() => setLiveTick((tick) => tick + 1)}
          />
        ),
      });
    },
    [showModal],
  );

  /** "Visualizar": o informativo do alerta. O prontuário fica no ID do paciente. */
  const handleView = useCallback(
    (alert: AlertRow) => {
      showModal({
        type: 'custom',
        data: (
          <AlertDetailsModal
            alert={alert}
            thresholds={thresholds}
            onOpenPatient={handleOpenPatient}
            onTreat={handleTreat}
            canTreat={canTreat}
          />
        ),
      });
    },
    [showModal, thresholds, handleOpenPatient, handleTreat, canTreat],
  );

  const handleOpenParameters = useCallback(() => {
    showModal({
      type: 'custom',
      data: <ParametersModal thresholds={thresholds} />,
    });
  }, [showModal, thresholds]);

  const handleChangePage = useCallback((next: number) => setPage(next), []);

  /** Mesmo gatilho do tempo real: recarrega lista, contadores e inatividade juntos. */
  const handleRetry = useCallback(() => setLiveTick((tick) => tick + 1), []);

  const content = (
    <Container fullscreen={fullscreen}>
      <Header>
        <GhostButton type="button" onClick={handleOpenParameters}>
          Ver parâmetros
        </GhostButton>
        <GhostButton
          type="button"
          onClick={() => setFullscreen((current) => !current)}
        >
          {fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
        </GhostButton>
      </Header>

      {(listFailed || summaryFailed) && (
        <FailureBox role="alert">
          <div>
            <strong>Não foi possível carregar os alertas.</strong>
            {listFailed
              ? 'A fila abaixo não está vazia: ela não pôde ser consultada.'
              : 'Os números do topo não puderam ser consultados.'}
          </div>
          <GhostButton type="button" onClick={handleRetry}>
            Tentar novamente
          </GhostButton>
        </FailureBox>
      )}

      <TopBlocks>
        <Summary
          summary={summary}
          inactivity={inactivity}
          periodLabel={summaryPeriodLabel}
          situationLabel={
            SITUATIONS.find((option) => option.value === situation)?.label ??
            'Todos'
          }
          failed={summaryFailed}
        />
      </TopBlocks>

      <Card>
        <CardTitle>Histórico de Alertas</CardTitle>

        <Filters>
          <label htmlFor="alert-situation">Situação</label>
          <select
            id="alert-situation"
            value={situation}
            onChange={(event) => {
              setSituation(event.target.value as AlertSituationEnum | '');
              setPage(1);
            }}
          >
            {SITUATIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <label htmlFor="alert-severity">Gravidade</label>
          <select
            id="alert-severity"
            value={severity}
            onChange={(event) => {
              setSeverity(event.target.value as AlertSeverityEnum | '');
              setPage(1);
            }}
          >
            <option value="">Todas</option>
            <option value={AlertSeverityEnum.SEVERE}>Grave</option>
            <option value={AlertSeverityEnum.MODERATE}>Moderado</option>
          </select>

          <label htmlFor="alert-category">Categoria</label>
          <select
            id="alert-category"
            value={category}
            onChange={(event) => {
              setCategory(event.target.value as AlertCategoryEnum | '');
              setPage(1);
            }}
          >
            <option value="">Todas</option>
            <option value={AlertCategoryEnum.BODY_SIGNS}>
              Sinais do Corpo
            </option>
            <option value={AlertCategoryEnum.LOW_SCORE}>Scores baixos</option>
            <option value={AlertCategoryEnum.ENGAGEMENT}>Engajamento</option>
          </select>

          <label htmlFor="alert-period">Período</label>
          <select
            id="alert-period"
            value={periodDays}
            onChange={(event) => {
              setPeriodDays(event.target.value);
              setPage(1);
            }}
          >
            {PERIODS.map((period) => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </select>
        </Filters>
      </Card>

      {loading ? (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      ) : listFailed ? (
        // O aviso já está no topo; aqui só não se desenha "Nenhum alerta".
        <EmptyBox>
          <CardContentNothingToDisplay>
            <img src={CloudSketchesImage} alt="" />
            Fila indisponível — não foi possível consultá-la
          </CardContentNothingToDisplay>
        </EmptyBox>
      ) : alerts.length > 0 ? (
        <>
          <AlertsTable
            alerts={alerts}
            thresholds={thresholds}
            onOpenPatient={handleOpenPatient}
            onView={handleView}
            onTreat={handleTreat}
            canTreat={canTreat}
          />

          <Pagination
            totalPages={Math.ceil(total / PER_PAGE)}
            onChangePage={handleChangePage}
            page={page}
          />
        </>
      ) : (
        <EmptyBox>
          <CardContentNothingToDisplay>
            <img src={CloudSketchesImage} alt="" />
            Nenhum alerta no período
          </CardContentNothingToDisplay>
        </EmptyBox>
      )}
    </Container>
  );

  // Em tela cheia o template sai do caminho: menu e cabeçalho roubariam a área que
  // a projeção precisa, e ninguém navega enquanto a fila está no telão.
  return fullscreen ? (
    content
  ) : (
    <DashboardTemplate name={MainAreasEnum.ALERTS}>
      <PageTree pages={pages} />
      {content}
    </DashboardTemplate>
  );
};

export default AlertsList;
